'use client';

import { useEffect } from 'react';
import {
  clearAnalyticsStorage,
  COOKIE_CONSENT_EVENT,
  COOKIE_CONSENT_KEY,
  getConsentExpiryDelay,
  getCookieConsentExpiry,
  hasAnalyticsConsent,
} from '@/lib/cookie-consent';

const GA_MEASUREMENT_ID = 'G-0KHXZ976F8';
const GTM_SCRIPT_ID = 'jamsom-gtm-script';
const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID?.trim();

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

function disableGoogleTagManager() {
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  window.gtag = undefined;
  document.getElementById(GTM_SCRIPT_ID)?.remove();
  window.dataLayer = [];
  clearAnalyticsStorage();
}

function enableGoogleTagManager() {
  if (!GTM_CONTAINER_ID || document.getElementById(GTM_SCRIPT_ID)) return;

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  window.dataLayer = window.dataLayer || [];
  // Match Google's Consent Mode command queue so GTM processes consent before startup.
  window.gtag = function gtag() {
    // GTM's command queue requires the function's Arguments object.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_CONTAINER_ID)}`;
  document.head.appendChild(script);
}

/** Loads GTM only after affirmative analytics consent and removes it immediately on withdrawal. */
export function GoogleTagManagerProvider() {
  useEffect(() => {
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      if (hasAnalyticsConsent()) enableGoogleTagManager();
      else disableGoogleTagManager();
      if (expiryTimer) clearTimeout(expiryTimer);
      const expiry = getCookieConsentExpiry();
      if (expiry) expiryTimer = setTimeout(sync, getConsentExpiryDelay(expiry));
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === COOKIE_CONSENT_KEY || event.key === null) sync();
    };
    sync();
    window.addEventListener(COOKIE_CONSENT_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      if (expiryTimer) clearTimeout(expiryTimer);
      window.removeEventListener(COOKIE_CONSENT_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return null;
}
